import React from 'react';
import classNames from 'classnames';
import { ChangeFieldHandlerType, CustomTooltipTypes } from 'models';
import {
	MuiCustomTooltip,
	MuiTooltipPositionType,
} from 'components/base/tooltip/MuiCustomTooltip';
import { IIconProps } from 'components/icons';
import { Loader } from 'components/loader/Loader';

import './input.styles.scss';

export interface CustomInputProps {
	name?: string;
	label?: string;
	id?: string;
	type?: string;
	required?: boolean;
	wrapperClass?: string;
	hintText?: string;
	error?: string;
	autoComplete?: string;
	hideValue?: boolean;
	onChange?: ChangeFieldHandlerType;
	onBlur?: (e: React.FocusEvent) => void;
	onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onlyNumbers?: true;
	endIcon?: React.FC<IIconProps>;
	endButton?: string;
	onClickEndButton?: (v: string) => void;
	value?: string | number;
	defaultValue?: string | number;
	placeholder?: string;
	postValueText?: string | number;
	inputValue?: string;
	maxLength?: number;
	autoFocus?: boolean;
	focusedInput?: string;
	pattern?: string;
	tooltipText?: string;
	tooltipPosition?: MuiTooltipPositionType;
	errorTooltipText?: string;
	isLoading?: boolean;
	disabled?: boolean;
}

export const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
	(
		{
			name,
			focusedInput,
			type = 'text',
			id,
			required,
			label,
			wrapperClass = '',
			hintText,
			autoComplete,
			onBlur,
			onFocus,
			onlyNumbers,
			endIcon: EndIcon,
			endButton,
			onClickEndButton,
			onChange,
			value = undefined,
			error,
			defaultValue,
			placeholder,
			postValueText,
			inputValue,
			maxLength,
			pattern,
			tooltipText,
			tooltipPosition,
			errorTooltipText,
			isLoading,
			disabled,
		},
		ref,
	) => {
		const handleEndButtonClick = () => {
			if (onClickEndButton) {
				onClickEndButton((value as string) || '');
			}
		};

		function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
			if (onlyNumbers && onChange) {
				onChange({
					...e,
					target: {
						...e.target,
						value: e.target.value.replace(/\D/, ''),
					},
				});
			} else if (onChange) {
				onChange(e);
			}
		}

		return (
			<div
				className={classNames('group', wrapperClass, {
					error,
				})}
			>
				<Loader
					isLoading={isLoading}
					wrapperClass={isLoading ? 'loader-input-wrapper' : ''}
				>
					{EndIcon ? (
						<EndIcon iconClass="group__icon group__icon--end" />
					) : null}
					{!!(
						(errorTooltipText || postValueText?.toString()) &&
						inputValue
					) && (
						<>
							<p className="group__postfix">
								<span
									style={{
										color: 'transparent',
										paddingRight: '5px',
										maxWidth: 'calc(100% - 56px)',
										overflow: 'hidden',
									}}
								>
									{`${inputValue} `}
								</span>
								{postValueText ? `/ ${postValueText}` : ''}
								{errorTooltipText ? (
									<MuiCustomTooltip
										title={<p>{errorTooltipText}</p>}
										type={CustomTooltipTypes.warning}
										className="group__tooltip-error"
									/>
								) : (
									''
								)}
							</p>
						</>
					)}
					<input
						onChange={handleChange}
						type={onlyNumbers ? 'tel' : type}
						name={name}
						id={id}
						autoComplete={autoComplete}
						className={classNames('group__input', {
							'group__input--pr': postValueText,
						})}
						placeholder={placeholder || ' '}
						onFocus={onFocus}
						value={value || ''}
						ref={ref}
						defaultValue={defaultValue}
						maxLength={maxLength}
						onBlur={onBlur}
						pattern={pattern}
						disabled={disabled}
					/>
				</Loader>
				{endButton ? (
					<button
						type="button"
						className="group__btn"
						onClick={handleEndButtonClick}
					>
						{endButton}
					</button>
				) : null}
				<div
					className="group__label-bg"
					data-label={required && label ? `${label}*` : label}
				/>
				<label
					className={classNames('group__label', {
						'hide-label': placeholder,
					})}
					htmlFor={id}
				>
					{required && label ? `${label}*` : label}
				</label>
				{!isLoading && (
					<p
						className={`group__hint font-caption ${
							error ? 'group__hint--error' : ''
						}`}
					>
						{error || hintText}
					</p>
				)}
				{tooltipText && (
					<MuiCustomTooltip
						placement={tooltipPosition}
						title={<p>{tooltipText}</p>}
						className="group__tooltip"
					/>
				)}
			</div>
		);
	},
);
